import { Controller } from "@hotwired/stimulus"

// Keeps an `<img>` pointing at /instagram_avatar/:handle alive across
// the cold-start window where the Rails endpoint 404s while a
// background job is still fetching the avatar from Instagram.
//
// On error the controller schedules a small number of retries with
// exponential backoff (each retry busts caching by appending a fresh
// `?_=timestamp` query param). After the budget is exhausted it
// falls back to hiding the element so the initial-letter bubble
// underneath stays visible.
//
// Markup:
//   <img src="/instagram_avatar/foo"
//        data-controller="lazy-avatar"
//        data-lazy-avatar-max-retries-value="4">
export default class extends Controller {
  static values = {
    maxRetries: { type: Number, default: 4 },
    baseDelayMs: { type: Number, default: 1500 }
  }

  connect() {
    this._retries = 0
    this._baseSrc = this.element.getAttribute("src") || ""
    this._onError = (e) => this._handleError(e)
    this.element.addEventListener("error", this._onError)
  }

  disconnect() {
    this.element.removeEventListener("error", this._onError)
    if (this._timer) clearTimeout(this._timer)
  }

  _handleError() {
    if (this._retries >= this.maxRetriesValue) {
      this.element.remove()
      return
    }
    this._retries++
    const delay = this.baseDelayMsValue * Math.pow(2, this._retries - 1)
    this._timer = setTimeout(() => {
      // Bust both browser and Cloudflare caches so a previously-200
      // (or 404'd no-store) response doesn't get reused.
      const sep = this._baseSrc.includes("?") ? "&" : "?"
      this.element.src = `${this._baseSrc}${sep}_=${Date.now()}`
    }, delay)
  }
}
